/**
 * El árbol de decisiones de calidad (docs/esquema.md §5.2.12), como función
 * pura: la MISMA la usa el teléfono para proponer la letra y el Worker para
 * decidirla con la regla vigente. Un test las compara caso por caso.
 *
 * Cambiar el orden del árbol es una versión nueva del algoritmo y de la regla; nunca se
 * reinterpretan muestras viejas. Versiones: **1** (5/9) cualquier adventicio presente va a
 * `adventicios_politica`; **2** (Agata, 14/9: «cualquier presencia de adventicios mayor a
 * “poco” debería decidir P o L») un adventicio en «poco» sigue el árbol y solo «bastante» o
 * «mucho» van a la política. La regla vigente dice con cuál se evalúa; gestión crea las
 * nuevas con `ALGORITMO_VIGENTE`.
 */

export const ALGORITMO_VIGENTE = 2;
export const ALGORITMOS_CONOCIDOS: readonly number[] = [1, ALGORITMO_VIGENTE];

export type LetraCalidad = 'Q' | 'P' | 'L' | 'descarte';

export interface ReglaCalidad {
	version: number;
	algoritmo_version: number;
	polvo_q_max: number;
	polvo_p_max: number;
	densidad_min: number;
	densidad_max: number;
	humedad_min: number;
	humedad_max: number;
	/** Qué letra decide un adventicio que cuenta: por ahora la analista (`requiere_decision`, Agata 14/9). */
	adventicios_politica: 'requiere_decision' | 'L' | 'P';
	tolerancia_suma_tamices: number | null;
}

export interface DefectoObservado {
	grupo: 'material_extranio' | 'adventicio';
	grado: 'P' | 'B' | 'M';
}

export interface MedicionDeMuestra {
	densidad: number;
	/** v6 (§12.9): null = todavía no hay humedad; el árbol saltea su criterio y la letra es provisoria. */
	humedad: number | null;
	tamiz_bajo_40: number;
	palos: 0 | 1;
	contaminacion_grave: 0 | 1;
	defectos: DefectoObservado[];
}

export interface Propuesta {
	/** `null` cuando la regla no decide (adventicios con política `requiere_decision`). */
	letra: LetraCalidad | null;
	/** Qué rama decidió, para mostrarla en el teléfono y en la ficha. */
	rama: string;
}

/** Primera pregunta con «sí» gana (árbol impreso, con los umbrales del 5/9 editables). */
export function proponerLetra(regla: ReglaCalidad, m: MedicionDeMuestra): Propuesta {
	if (!ALGORITMOS_CONOCIDOS.includes(regla.algoritmo_version)) throw new Error(`algoritmo de calidad desconocido: ${regla.algoritmo_version}`);
	const decidir = (letra: LetraCalidad | null, rama: string): Propuesta => ({ letra, rama });
	// Versión 2: «poco» no cuenta como adventicio que decida (Agata, 14/9).
	const cuentaAdventicio = (d: DefectoObservado) => d.grupo === 'adventicio' && (regla.algoritmo_version === 1 || d.grado !== 'P');

	if (m.contaminacion_grave === 1) return decidir('descarte', 'contaminacion_grave');
	if (m.tamiz_bajo_40 > regla.polvo_p_max) return decidir('L', 'polvo_sobre_p_max');
	if (m.defectos.some(cuentaAdventicio)) {
		if (regla.adventicios_politica === 'requiere_decision') return decidir(null, 'adventicios_requiere_decision');
		return decidir(regla.adventicios_politica, 'adventicios');
	}
	if (m.defectos.some((d) => d.grupo === 'material_extranio' && (d.grado === 'B' || d.grado === 'M'))) return decidir('P', 'materiales_extranios');
	if (m.palos === 1) return decidir('P', 'palos');
	if (m.tamiz_bajo_40 > regla.polvo_q_max) return decidir('P', 'polvo_sobre_q_max');
	if (m.densidad < regla.densidad_min || m.densidad > regla.densidad_max) return decidir('P', 'densidad');
	if (m.humedad !== null && (m.humedad < regla.humedad_min || m.humedad > regla.humedad_max)) return decidir('P', 'humedad');
	return decidir('Q', 'sin_observaciones');
}

/**
 * ¿La decisión de la analista necesita motivo? Cuando difiere de lo propuesto, o
 * cuando la regla no propuso nada. Es la misma condición del CHECK de `muestra`.
 * El apto Virginia no entra: es la letra (Q), derivado (gerenta, 7/9).
 */
export function motivoObligatorio(propuesta: Propuesta, asignada: LetraCalidad): boolean {
	return propuesta.letra === null || propuesta.letra !== asignada;
}

/** Suma de los cuatro tamices, en gramos sobre 100 g; fuera de 100 ± tolerancia se avisa (NULL: solo se informa). */
export function sumaDeTamices(t: { tamiz_sobre_3_5: number; tamiz_sobre_6: number; tamiz_sobre_40: number; tamiz_bajo_40: number }): number {
	return Math.round((t.tamiz_sobre_3_5 + t.tamiz_sobre_6 + t.tamiz_sobre_40 + t.tamiz_bajo_40) * 1000) / 1000;
}
